"use client";

import { useEffect, useMemo, useState } from "react";

import { SPECIES_DEX, type SpeciesData } from "@/data/species";
import { loadGame } from "@/engine/save-system";
import { BattleSprite } from "@/components/game/BattleSprite";
import { StatsScreen, type StatsScreenMember } from "@/components/game/StatsScreen";

// ---------------------------------------------------------------------------
// Palette — see PIXEL_STYLE.md
// ---------------------------------------------------------------------------

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const WALNUT = "#5c4a30";
const SAND = "#b89968";
const GOLD = "#c5a83a";
const MOSS = "#7a9a4a";

const TYPE_COLORS: Record<string, string> = {
  sales: "#b8704a",
  tech: "#4a7eb8",
  finance: "#c5a83a",
  creative: "#a04ab8",
  ops: "#7a8a4a",
  normal: "#8a8a8a",
};

function typeColor(type: string): string {
  return TYPE_COLORS[type.toLowerCase()] ?? TYPE_COLORS.normal;
}

function padId(n: number): string {
  return `#${String(n).padStart(3, "0")}`;
}

// ---------------------------------------------------------------------------
// Filter + sort types
// ---------------------------------------------------------------------------

type Filter = "all" | "seen" | "caught" | "type";
const FILTERS: Filter[] = ["all", "seen", "caught", "type"];

type Sort = "id" | "name" | "level" | "type";
const SORTS: Sort[] = ["id", "name", "level", "type"];

// Collect unique types from the dex (for type-cycle filter).
const ALL_TYPES: string[] = Array.from(
  new Set(
    SPECIES_DEX.flatMap((s) => [s.type1, s.type2].filter((t): t is string => !!t)),
  ),
).sort();

// ---------------------------------------------------------------------------
// Save-derived state
// ---------------------------------------------------------------------------

type Progress = {
  seenSpecies: Set<string>;
  caughtSpecies: Set<string>;
  partyLevels: Record<string, number>;
};

function emptyProgress(): Progress {
  return {
    seenSpecies: new Set(),
    caughtSpecies: new Set(),
    partyLevels: {},
  };
}

function loadProgress(): Progress {
  const save = loadGame("auto");
  if (!save) return emptyProgress();
  const partyLevels: Record<string, number> = {};
  for (const m of save.party) {
    if (!(m.speciesId in partyLevels) || partyLevels[m.speciesId] < m.level) {
      partyLevels[m.speciesId] = m.level;
    }
  }
  return {
    seenSpecies: new Set(save.progress.seenSpecies),
    caughtSpecies: new Set(save.progress.caughtSpecies),
    partyLevels,
  };
}

function isSpeciesIdMatch(species: SpeciesData, id: string): boolean {
  const lower = id.toLowerCase();
  const asNum = Number(id);
  return (
    species.id === asNum ||
    species.name.toLowerCase() === lower ||
    species.sprite.toLowerCase() === lower
  );
}

function hasProgressFor(set: Set<string>, species: SpeciesData): boolean {
  for (const id of set) {
    if (isSpeciesIdMatch(species, id)) return true;
  }
  return false;
}

function levelFor(progress: Progress, species: SpeciesData): number {
  for (const [id, lvl] of Object.entries(progress.partyLevels)) {
    if (isSpeciesIdMatch(species, id)) return lvl;
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function DexPage() {
  const [progress, setProgress] = useState<Progress>(() => emptyProgress());
  const [filter, setFilter] = useState<Filter>("all");
  const [typeIdx, setTypeIdx] = useState<number>(0);
  const [sort, setSort] = useState<Sort>("id");
  const [search, setSearch] = useState<string>("");
  const [selected, setSelected] = useState<SpeciesData | null>(null);

  // Load save on mount (client-only).
  useEffect(() => {
    setProgress(loadProgress());
  }, []);

  // Keyboard: Tab cycles filter (ignore inside the search input).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      const target = e.target as HTMLElement | null;
      if (target && target.tagName === "INPUT") return;
      e.preventDefault();
      setFilter((f) => FILTERS[(FILTERS.indexOf(f) + 1) % FILTERS.length]);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const seenCount = useMemo(
    () => SPECIES_DEX.filter((s) => hasProgressFor(progress.seenSpecies, s)).length,
    [progress.seenSpecies],
  );
  const caughtCount = useMemo(
    () => SPECIES_DEX.filter((s) => hasProgressFor(progress.caughtSpecies, s)).length,
    [progress.caughtSpecies],
  );
  const totalCount = SPECIES_DEX.length;

  const activeType = ALL_TYPES[typeIdx % ALL_TYPES.length] ?? "";

  const visible = useMemo(() => {
    const lowerQ = search.trim().toLowerCase();
    let list = SPECIES_DEX.slice();

    // Filter row
    if (filter === "seen") {
      list = list.filter((s) => hasProgressFor(progress.seenSpecies, s));
    } else if (filter === "caught") {
      list = list.filter((s) => hasProgressFor(progress.caughtSpecies, s));
    } else if (filter === "type") {
      list = list.filter((s) => s.type1 === activeType || s.type2 === activeType);
    }

    // Search (only matches if the entry is seen — otherwise name is hidden).
    if (lowerQ.length > 0) {
      list = list.filter(
        (s) =>
          hasProgressFor(progress.seenSpecies, s) &&
          s.name.toLowerCase().includes(lowerQ),
      );
    }

    // Sort
    const sorted = list.slice();
    if (sort === "id") {
      sorted.sort((a, b) => a.id - b.id);
    } else if (sort === "name") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort === "level") {
      sorted.sort((a, b) => levelFor(progress, b) - levelFor(progress, a));
    } else if (sort === "type") {
      sorted.sort((a, b) => a.type1.localeCompare(b.type1) || a.id - b.id);
    }
    return sorted;
  }, [filter, activeType, sort, search, progress]);

  // Stub member for StatsScreen modal — uses base stats if the species isn't in party.
  function memberFor(species: SpeciesData): StatsScreenMember {
    const lvl = levelFor(progress, species) || 5;
    const types = [species.type1, species.type2].filter((t): t is string => !!t);
    return {
      speciesId: species.sprite,
      nickname: species.name,
      level: lvl,
      xp: 0,
      hpCurrent: species.baseHp,
      hpMax: species.baseHp,
      moves: [],
      pp: {},
      stats: { atk: species.baseAtk, def: species.baseDef, spd: species.baseSpe },
      types,
    };
  }

  return (
    <main
      className="min-h-screen w-full"
      style={{
        background: CREAM,
        color: INK,
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        imageRendering: "pixelated",
      }}
    >
      <div className="mx-auto max-w-6xl px-6 py-8">
        {/* Header panel */}
        <header
          className="mb-6 px-4 py-3"
          style={{
            background: CREAM,
            border: `2px solid ${INK}`,
            boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
          }}
        >
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <h1
              className="font-bold uppercase tracking-widest"
              style={{ fontSize: "28px", color: INK, letterSpacing: "0.2em" }}
            >
              SURGGIE DEX
            </h1>
            <div className="flex gap-4 text-sm font-bold uppercase tracking-wider">
              <span style={{ color: GOLD }}>
                {caughtCount}/{totalCount} CAUGHT
              </span>
              <span style={{ color: WALNUT }}>
                {seenCount}/{totalCount} SEEN
              </span>
            </div>
          </div>
        </header>

        {/* Filter row */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {FILTERS.map((f) => {
            const active = f === filter;
            const label =
              f === "type"
                ? `TYPE: ${(activeType || "—").toUpperCase()}`
                : f.toUpperCase();
            return (
              <button
                key={f}
                type="button"
                onClick={() => {
                  if (f === "type" && filter === "type") {
                    setTypeIdx((i) => (i + 1) % ALL_TYPES.length);
                  } else {
                    setFilter(f);
                  }
                }}
                className="px-3 py-1 text-xs font-bold uppercase tracking-widest"
                style={{
                  background: active ? INK : CREAM,
                  color: active ? CREAM : INK,
                  border: `2px solid ${INK}`,
                  boxShadow: active
                    ? `inset 1px 1px 0 ${WALNUT}`
                    : `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            );
          })}

          <div className="ml-auto flex items-center gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="SEARCH..."
              className="px-2 py-1 text-xs font-bold uppercase tracking-widest outline-none"
              style={{
                background: CREAM,
                color: INK,
                border: `2px solid ${INK}`,
                boxShadow: `inset 1px 1px 0 ${WALNUT}`,
                width: "160px",
                fontFamily: "inherit",
              }}
            />
            <label
              className="flex items-center gap-1 text-xs font-bold uppercase tracking-widest"
              style={{ color: INK }}
            >
              SORT
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value as Sort)}
                className="px-2 py-1 text-xs font-bold uppercase tracking-widest outline-none"
                style={{
                  background: CREAM,
                  color: INK,
                  border: `2px solid ${INK}`,
                  fontFamily: "inherit",
                }}
              >
                {SORTS.map((s) => (
                  <option key={s} value={s}>
                    {s.toUpperCase()}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        <p
          className="mb-4 text-[10px] uppercase tracking-widest"
          style={{ color: WALNUT }}
        >
          TAB CYCLES FILTER · CLICK CARD FOR STATS
        </p>

        {/* Grid */}
        <div
          className="grid gap-3"
          style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}
        >
          {visible.map((s) => {
            const seen = hasProgressFor(progress.seenSpecies, s);
            const caught = hasProgressFor(progress.caughtSpecies, s);
            return (
              <DexCell
                key={s.id}
                species={s}
                seen={seen}
                caught={caught}
                onClick={() => setSelected(s)}
              />
            );
          })}
          {visible.length === 0 && (
            <div
              className="col-span-4 px-4 py-8 text-center text-sm font-bold uppercase tracking-widest"
              style={{
                background: CREAM,
                color: WALNUT,
                border: `2px dashed ${WALNUT}`,
              }}
            >
              NO MATCHES
            </div>
          )}
        </div>
      </div>

      {selected && (
        <StatsScreen
          open={true}
          member={memberFor(selected)}
          onClose={() => setSelected(null)}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Dex cell
// ---------------------------------------------------------------------------

function DexCell({
  species,
  seen,
  caught,
  onClick,
}: {
  species: SpeciesData;
  seen: boolean;
  caught: boolean;
  onClick: () => void;
}) {
  const borderColor = caught ? GOLD : INK;
  const borderWidth = caught ? 3 : 2;

  return (
    <button
      type="button"
      onClick={onClick}
      className="text-left transition-transform hover:-translate-y-0.5"
      style={{
        background: CREAM,
        color: INK,
        border: `${borderWidth}px solid ${borderColor}`,
        boxShadow: `inset 1px 1px 0 ${CREAM}, inset -1px -1px 0 ${WALNUT}`,
        cursor: "pointer",
        padding: "10px",
        fontFamily: "inherit",
      }}
      aria-label={seen ? species.name : `Unknown #${species.id}`}
    >
      <div
        className="relative mx-auto mb-2 flex items-center justify-center"
        style={{
          width: "72px",
          height: "72px",
          background: SAND,
          border: `2px solid ${INK}`,
          overflow: "hidden",
        }}
      >
        {seen ? (
          <BattleSprite
            speciesId={species.sprite}
            orientation="front"
            scale={2}
            battlePortrait
          />
        ) : (
          <SilhouetteSprite speciesId={species.sprite} />
        )}
      </div>

      <div
        className="text-center text-[11px] font-bold uppercase tracking-widest"
        style={{ color: WALNUT }}
      >
        {padId(species.id)}
      </div>
      <div
        className="text-center text-xs font-bold uppercase tracking-widest"
        style={{ color: seen ? INK : WALNUT, minHeight: "16px" }}
      >
        {seen ? species.name : "???"}
      </div>

      {seen && (
        <div className="mt-1 flex justify-center gap-1">
          <TypeBadge type={species.type1} />
          {species.type2 && <TypeBadge type={species.type2} />}
        </div>
      )}
    </button>
  );
}

function TypeBadge({ type }: { type: string }) {
  return (
    <span
      className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest"
      style={{
        background: typeColor(type),
        color: CREAM,
        border: `1px solid ${INK}`,
      }}
    >
      {type.toUpperCase()}
    </span>
  );
}

/**
 * Silhouette renderer — wraps BattleSprite in a div that paints the sprite
 * solid ink via a CSS filter. Pure ink #1a1a1a per PIXEL_STYLE.md.
 */
function SilhouetteSprite({ speciesId }: { speciesId: string }) {
  return (
    <div
      aria-hidden
      style={{
        filter:
          "brightness(0) saturate(100%) invert(8%) sepia(7%) saturate(0%) hue-rotate(0deg)",
      }}
    >
      <BattleSprite speciesId={speciesId} orientation="front" scale={2} battlePortrait />
    </div>
  );
}
